import {
  lazy,
  Suspense,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Link, useLocation } from 'react-router-dom';
import { publicBluesky } from '../../data/public-bluesky';
import { getAuthenticatedAuthorFeed } from '../../data/authenticated';
import { getProfileComments } from '../../data/profile-comments';
import {
  loadSkyspaceProfileTheme,
  queryKeys,
} from '../../data/queries';
import {
  getRecentSkyspacePeople,
  getSiteModeration,
  profileShareUrl,
  profileShareName,
  trackSiteEvent,
} from '../../data/site-services';
import {
  isSupporter as isSkyspaceSupporter,
  loadSupporters,
} from '../../data/supporters';
import { openSupportDialog } from '../../data/support';
import {
  classicPanelColour,
  extractPictures,
  extractVideos,
  friendComments,
  legacyWhoToMeet,
  mergeProfileAesthetics,
  normalizeClassicTheme,
  parseInterests,
  parseLegacyAesthetics,
  parseMusic,
  parseTop8Handles,
  parseTop8Slots,
  regularPosts,
  selectTopFriendSlots,
  type ClassicTheme,
} from '../../domain/classic-profile';
import type {
  ActorProfile,
  CustomPanel,
  FeedPost,
  ProfileMusic,
} from '../../domain/types';
import {
  customPanelIdFromSection,
  customPanelSlots,
  isProfileSectionId,
  normalizeProfileLayout,
  type ProfileLayout,
  type ProfileLayoutSectionId,
  type ProfileSectionId,
} from '../../domain/profile-layout';
import { chatroomsEnabled } from '../../domain/chatrooms';
import { shouldHideActor, shouldHidePost, visibleActors } from '../../domain/moderation';
import { postSearch, profileSearch, searchSearch } from '../../lib/route';
import {
  shadertoyId,
  shadertoyPreviewUrl,
} from '../../lib/shadertoy';
import {
  customShaderSource,
  type SkyspaceShader,
} from '../../lib/shaders';
import { ErrorState } from '../../ui/AsyncState';
import {
  CommentCard,
  formatDate,
  PostEmbed,
  PostInteractions,
  PostsSection,
  Replies,
  RichText,
  TopFriends,
} from './ProfileSections';
import { SearchProfileCard } from '../search/SearchPage';
import { startCursorEffect } from './cursor-effect';
import {
  backgroundAssetUrl,
  cssFontStack,
  ensureGoogleFontLink,
} from './profile-theme';
import {
  DirectBlurbEditor,
  FollowButton,
  InterestsEditor,
  LeaveComment,
  MusicEditor,
  NewPostControl,
  ProfileEditor,
  QuoteEditor,
  quoteFitClass,
} from './AuthenticatedControls';
import { useAuth } from '../../auth/AuthContext';
import { useSettings } from '../../settings/SettingsContext';

const Top8Picker = lazy(() => import('./Top8Picker').then(module => ({
  default: module.Top8Picker,
})));
const CustomProfilePanel = lazy(() => import('./CustomProfilePanel').then(module => ({
  default: module.CustomProfilePanel,
})));
const MediaModal = lazy(() => import('./MediaModal').then(module => ({
  default: module.MediaModal,
})));
interface LayoutPreview {
  layout: ProfileLayout;
  customPanels: CustomPanel[];
}

function count(value: number | undefined): string {
  return new Intl.NumberFormat('en-GB').format(value || 0);
}

export function shouldShowSkyspaceWelcome(
  profileHandle: string,
  requestedHandle: string,
  viewerDid?: string,
): boolean {
  return !viewerDid && (
    profileHandle.toLowerCase() === 'skyspace.me'
    || requestedHandle.replace(/^@/, '').toLowerCase() === 'skyspace.me'
  );
}

function activityStatus(createdAt?: string): { label: string; online: boolean } {
  const date = createdAt ? new Date(createdAt) : undefined;
  if (!date || Number.isNaN(date.getTime())) return { label: 'No posts available.', online: false };
  const online = Date.now() - date.getTime() < 6 * 60 * 60 * 1000;
  return {
    label: online
      ? 'Online now!'
      : `Seen on: ${new Intl.DateTimeFormat('en-GB').format(date)}`,
    online,
  };
}

export function adaptiveProfileInfoGap(
  availableHeight: number,
  childHeights: number[],
  preferredGap: number,
): number {
  const gaps = Math.max(0, childHeights.length - 1);
  if (gaps === 0) return 0;
  const contentHeight = childHeights.reduce((total, height) => total + height, 0);
  const availableGap = (availableHeight - contentHeight) / gaps;
  return Math.max(0, Math.min(preferredGap, Math.floor(availableGap * 100) / 100));
}

function useAdaptiveProfileInfoSpacing(dependency: string) {
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const content = contentRef.current;
    const container = content?.parentElement;
    if (!content || !container) return undefined;

    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const containerStyle = getComputedStyle(container);
        const availableHeight = container.clientHeight
          - Number.parseFloat(containerStyle.paddingTop)
          - Number.parseFloat(containerStyle.paddingBottom);
        const preferredGap = matchMedia('(max-width: 768px)').matches ? 8 : 15;
        const status = content.querySelector<HTMLElement>('#last-login');
        if (status) {
          status.style.removeProperty('font-size');
          const availableWidth = content.clientWidth;
          const naturalWidth = status.scrollWidth;
          if (naturalWidth > availableWidth) {
            const defaultSize = Number.parseFloat(getComputedStyle(status).fontSize);
            status.style.fontSize = `${Math.max(
              8,
              Math.floor((defaultSize * availableWidth / naturalWidth) * 100) / 100,
            )}px`;
          }
        }
        const childHeights = Array.from(content.children)
          .map(child => child.getBoundingClientRect().height);
        const gap = adaptiveProfileInfoGap(availableHeight, childHeights, preferredGap);
        content.style.gap = `${gap}px`;
      });
    };

    const observer = new ResizeObserver(update);
    observer.observe(container);
    observer.observe(content);
    Array.from(content.children).forEach(child => observer.observe(child));
    window.addEventListener('resize', update);
    void document.fonts?.ready.then(update);
    update();

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [dependency]);

  return contentRef;
}

function ShaderBackground({
  shader,
  theme,
  animate,
}: {
  shader: SkyspaceShader;
  theme: ClassicTheme;
  animate: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [imageCode, setImageCode] = useState(() => customShaderSource(shader));
  const [rendererFailed, setRendererFailed] = useState(false);

  useEffect(() => {
    if (shader.preset === 'custom') {
      setImageCode(customShaderSource(shader));
      return undefined;
    }
    let disposed = false;
    setImageCode('');
    void import('../../lib/shader-presets').then(({ shaderPresetSource }) => {
      if (!disposed) setImageCode(shaderPresetSource(shader.preset));
    });
    return () => {
      disposed = true;
    };
  }, [shader.code, shader.preset]);

  useEffect(() => {
    setRendererFailed(false);
  }, [imageCode]);

  useEffect(() => {
    if (!canvasRef.current || !imageCode || rendererFailed) {
      return undefined;
    }
    let disposed = false;
    let renderer: { destroy(): void } | undefined;
    const canvas = canvasRef.current;
    void import('./shader-renderer').then(({ startShaderRenderer }) => {
      if (disposed) return;
      try {
        renderer = startShaderRenderer({
          canvas,
          imageCode,
          animate,
        });
      } catch (error) {
        console.warn('SkySpace could not render this shader.', error);
        setRendererFailed(true);
      }
    });
    return () => {
      disposed = true;
      renderer?.destroy();
    };
  }, [animate, imageCode, rendererFailed]);

  const rendered = Boolean(imageCode && !rendererFailed);
  return createPortal(
    <>
      <div
        className="modern-profile-background skyspace-shader-fallback"
        data-shader-status={rendered ? 'loading' : 'fallback'}
        style={{
          '--shader-primary': theme.frameColor,
          '--shader-secondary': theme.titleColor,
          backgroundColor: theme.backgroundColor,
        } as CSSProperties}
        aria-hidden="true"
      />
      {rendered && (
        <canvas
          ref={canvasRef}
          className="modern-profile-background modern-shader-canvas"
          aria-hidden="true"
        />
      )}
    </>,
    document.body,
  );
}

function useIdleSections(): boolean {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if ('requestIdleCallback' in window) {
      const id = window.requestIdleCallback(() => setReady(true), { timeout: 1200 });
      return () => window.cancelIdleCallback(id);
    }
    const id = globalThis.setTimeout(() => setReady(true), 600);
    return () => globalThis.clearTimeout(id);
  }, []);
  return ready;
}

export { cssFontStack };

const PROFILE_THEME_CACHE_PREFIX = 'skyspace-profile-theme:';


function cachedProfileTheme(handle: string): ClassicTheme | undefined {
  try {
    const value = localStorage.getItem(`${PROFILE_THEME_CACHE_PREFIX}${handle.toLowerCase()}`);
    if (!value) return undefined;
    const parsed = JSON.parse(value) as Partial<ClassicTheme>;
    if (
      typeof parsed.font !== 'string'
      || typeof parsed.bodyColor !== 'string'
      || typeof parsed.backgroundKind !== 'string'
    ) return undefined;
    return {
      ...normalizeClassicTheme(undefined),
      ...parsed,
      backgroundColors: Array.isArray(parsed.backgroundColors)
        ? parsed.backgroundColors
        : normalizeClassicTheme(undefined).backgroundColors,
    } as ClassicTheme;
  } catch {
    return undefined;
  }
}

function cacheProfileTheme(handle: string, theme: ClassicTheme): void {
  try {
    localStorage.setItem(
      `${PROFILE_THEME_CACHE_PREFIX}${handle.toLowerCase()}`,
      JSON.stringify(theme),
    );
  } catch {
    // A private or full browser store should never prevent the profile loading.
  }
}

function usePreparedGoogleFont(font: string): boolean {
  const [loadedFont, setLoadedFont] = useState(() => (
    font.toLowerCase() === 'arial' ? font : ''
  ));

  useEffect(() => {
    if (font.toLowerCase() === 'arial') {
      setLoadedFont(font);
      return;
    }
    let disposed = false;
    const link = ensureGoogleFontLink(font);
    const finish = () => {
      if (!disposed) setLoadedFont(font);
    };
    const loadFont = () => {
      void document.fonts.load(`16px ${cssFontStack(font)}`).then(finish, finish);
    };
    if (document.fonts.check(`16px ${cssFontStack(font)}`)) {
      finish();
      return () => {
        disposed = true;
      };
    }
    link?.addEventListener('load', loadFont, { once: true });
    if (link?.sheet) loadFont();
    const timeout = window.setTimeout(finish, 1_200);
    return () => {
      disposed = true;
      window.clearTimeout(timeout);
      link?.removeEventListener('load', loadFont);
    };
  }, [font]);

  return font.toLowerCase() === 'arial' || loadedFont === font;
}

export function cyclingBackgroundCss(colours: string[]): string {
  const properties = colours.map((colour, index) => (
    `@property --skyspace-cycle-${index} { syntax: "<color>"; inherits: false; initial-value: ${colour}; }`
  ));
  const frames = [...colours.keys(), colours.length].map(step => {
    const percentage = Math.round((step / colours.length) * 100);
    const values = colours.map((_, index) => (
      `--skyspace-cycle-${index}: ${colours[(index + step) % colours.length]};`
    ));
    return `${percentage}% { ${values.join(' ')} }`;
  });
  return `${properties.join(' ')} @keyframes skyspace-background-cycle { ${frames.join(' ')} }`;
}

export function cyclingDurationSeconds(speed: number): number {
  const boundedSpeed = Math.max(0, Math.min(100, speed));
  return Number((24 - (boundedSpeed / 100) * 20).toFixed(2));
}

export function selectWeightedRecentPeople<T>(
  candidates: T[],
  seed: number,
  limit = 3,
): T[] {
  const pool = [...candidates];
  const selected: T[] = [];
  let state = Math.max(1, Math.floor(Math.abs(seed % 1) * 0x7fffffff));
  const random = () => {
    state = (state * 48271) % 0x7fffffff;
    return state / 0x7fffffff;
  };
  while (pool.length > 0 && selected.length < limit) {
    const totalWeight = pool.reduce((total, _, index) => total + pool.length - index, 0);
    let target = random() * totalWeight;
    let selectedIndex = 0;
    for (let index = 0; index < pool.length; index += 1) {
      target -= pool.length - index;
      if (target <= 0) {
        selectedIndex = index;
        break;
      }
    }
    selected.push(pool.splice(selectedIndex, 1)[0]);
  }
  return selected;
}

function CustomizationFrame({
  theme,
  children,
  effectsEnabled = true,
}: {
  theme: ClassicTheme;
  children: React.ReactNode;
  effectsEnabled?: boolean;
}) {
  const { settings } = useSettings();
  const effectsDisabled = !effectsEnabled || settings.reduceMotion || settings.disableCustomEffects;
  useEffect(() => {
    const root = document.documentElement;
    const bodyStyle = document.body.getAttribute('style');
    const hadCustomCursor = document.body.classList.contains('custom-cursor');
    const previousVariables = {
      font: root.style.getPropertyValue('--primary-font-color'),
      title: root.style.getPropertyValue('--primary-title-color'),
      link: root.style.getPropertyValue('--primary-link-color'),
      panel: root.style.getPropertyValue('--panel-bg'),
    };
    root.style.setProperty('--primary-font-color', theme.bodyColor);
    root.style.setProperty('--primary-title-color', theme.titleColor);
    root.style.setProperty('--primary-link-color', theme.linkColor);
    root.style.setProperty('--panel-bg', classicPanelColour(theme));
    document.body.style.backgroundColor = (
      theme.backgroundKind === 'video'
      || theme.backgroundKind === 'shader'
      || theme.backgroundKind === 'legacy-shadertoy'
    ) ? '#000000' : theme.backgroundColor;
    document.body.style.animation = '';
    const hasCustomCursor = !effectsDisabled && Boolean(theme.cursor?.startsWith('http'));
    document.body.classList.toggle('custom-cursor', hasCustomCursor);
    document.body.style.setProperty(
      'cursor',
      effectsDisabled ? 'auto' : hasCustomCursor ? `url("${theme.cursor}"), auto` : theme.cursor || '',
      'important',
    );

    let backgroundAnimation: HTMLStyleElement | undefined;
    if (theme.backgroundKind === 'image' && theme.backgroundUrl) {
      const cornerPosition = theme.backgroundPosition.includes(' ');
      document.body.style.backgroundImage = `url("${backgroundAssetUrl(theme.backgroundUrl)}")`;
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.backgroundRepeat = theme.backgroundPosition === 'tiled' ? 'repeat' : 'no-repeat';
      document.body.style.backgroundSize = theme.backgroundPosition === 'tiled'
        ? 'auto'
        : theme.backgroundPosition === 'contain' || cornerPosition
          ? 'contain'
          : 'cover';
      document.body.style.backgroundPosition = cornerPosition ? theme.backgroundPosition : 'center';
    } else if (theme.backgroundKind === 'legacy-shadertoy' && theme.backgroundUrl) {
      const legacyShaderId = shadertoyId(theme.backgroundUrl);
      document.body.style.backgroundImage = legacyShaderId
        ? `url("${shadertoyPreviewUrl(legacyShaderId)}")`
        : 'none';
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.backgroundRepeat = 'no-repeat';
      document.body.style.backgroundSize = 'cover';
      document.body.style.backgroundPosition = 'center';
    } else if (theme.backgroundKind === 'color') {
      const colours = theme.backgroundColors;
      const renderedColours = theme.backgroundColorCycle
        ? colours.map((colour, index) => {
          document.body.style.setProperty(`--skyspace-cycle-${index}`, colour);
          return `var(--skyspace-cycle-${index})`;
        })
        : colours;
      const angle = `${theme.backgroundGradientAngle}deg`;
      if (theme.backgroundColorMode === 'pattern') {
        document.body.style.backgroundImage = theme.backgroundPattern === 'checks'
          ? `conic-gradient(${renderedColours[0]} 25%, ${renderedColours[1]} 0 50%, ${renderedColours[0]} 0 75%, ${renderedColours[1]} 0)`
          : `repeating-linear-gradient(${angle}, ${renderedColours[0]} 0 14px, ${renderedColours[1]} 14px 28px)`;
        document.body.style.backgroundSize = theme.backgroundPattern === 'checks'
          ? '36px 36px'
          : 'auto';
      } else if (theme.backgroundColorMode === 'gradient') {
        document.body.style.backgroundImage = `linear-gradient(${angle}, ${renderedColours.join(', ')})`;
        document.body.style.backgroundSize = 'cover';
      } else {
        document.body.style.backgroundImage = 'none';
        document.body.style.backgroundColor = renderedColours[0];
      }
      document.body.style.backgroundAttachment = 'fixed';
      document.body.style.backgroundRepeat = 'repeat';
      document.body.style.backgroundPosition = 'center';
      if (theme.backgroundColorCycle && !settings.reduceMotion) {
        backgroundAnimation = document.createElement('style');
        backgroundAnimation.textContent = cyclingBackgroundCss(colours);
        document.head.appendChild(backgroundAnimation);
        document.body.style.animation = `skyspace-background-cycle ${
          cyclingDurationSeconds(theme.backgroundColorCycleSpeed)
        }s linear infinite`;
      }
    } else {
      document.body.style.backgroundImage = 'none';
    }

    ensureGoogleFontLink(theme.font);

    return () => {
      if (bodyStyle === null) document.body.removeAttribute('style');
      else document.body.setAttribute('style', bodyStyle);
      document.body.classList.toggle('custom-cursor', hadCustomCursor);
      root.style.setProperty('--primary-font-color', previousVariables.font);
      root.style.setProperty('--primary-title-color', previousVariables.title);
      root.style.setProperty('--primary-link-color', previousVariables.link);
      root.style.setProperty('--panel-bg', previousVariables.panel);
      backgroundAnimation?.remove();
    };
  }, [effectsDisabled, settings.reduceMotion, theme]);

  useEffect(() => {
    if (effectsDisabled || !theme.cursorTrail) return;
    let disposed = false;
    let instance: { destroy(): void } | undefined;
    void startCursorEffect(theme.cursorTrail, theme.cursorTrailParameters).then(value => {
      if (disposed) value?.destroy();
      else instance = value;
    });
    return () => {
      disposed = true;
      instance?.destroy();
    };
  }, [effectsDisabled, theme.cursorTrail, theme.cursorTrailParameters]);

  const style = {
    '--modern-panel-bg': classicPanelColour(theme),
    '--modern-frame': `${theme.frameWidth}px ${theme.frameStyle} ${theme.frameColor}`,
    '--modern-frame-radius': `${theme.frameRadius}px`,
    '--modern-separator': theme.frameColor,
    fontFamily: cssFontStack(theme.font),
  } as CSSProperties;

  return (
    <div className={`classic-profile-scope${theme.customized ? ' is-customized' : ''}`} style={style}>
      {!settings.reduceMotion && theme.backgroundKind === 'video' && theme.backgroundUrl && createPortal(
        <video
          className="modern-profile-background"
          src={backgroundAssetUrl(theme.backgroundUrl)}
          autoPlay
          muted
          loop
          playsInline
          aria-hidden="true"
        />,
        document.body,
      )}
      {theme.backgroundKind === 'shader' && theme.shader && (
        <ShaderBackground
          shader={theme.shader}
          theme={theme}
          animate={!effectsDisabled}
        />
      )}
      {theme.backgroundKind === 'shader'
        && theme.shader?.attribution
        && theme.shader.attributionUrl && (
        <a
          className="modern-shader-credit"
          href={theme.shader.attributionUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          Shader: {theme.shader.attribution}
        </a>
      )}
      {children}
    </div>
  );
}

function ProfileLoadingSkeleton({
  handle,
  profile,
  theme,
  layout,
}: {
  handle: string;
  profile?: ActorProfile;
  theme: ClassicTheme;
  layout: ProfileLayout;
}) {
  const panelClass = 'modern-framed-panel modern-profile-skeleton-panel';
  const sections: Record<ProfileSectionId, ReactNode> = {
    music: (
      <section id="music" className={`${panelClass} modern-profile-skeleton-music`} key="music">
        <span className="modern-skeleton-line is-short" />
      </section>
    ),
    interests: (
      <section id="interests-section" className={`${panelClass} modern-profile-skeleton-interests`} key="interests">
        <span className="modern-skeleton-line is-title" />
        <span className="modern-skeleton-line" />
        <span className="modern-skeleton-line" />
      </section>
    ),
    latest: (
      <section id="latest-post" className={`${panelClass} modern-profile-skeleton-post`} key="latest">
        <span className="modern-skeleton-line is-title" />
        <span className="modern-skeleton-line is-short" />
        <span className="modern-skeleton-line" />
        <span className="modern-skeleton-line" />
        <span className="modern-skeleton-line is-short" />
      </section>
    ),
    blurbs: (
      <section id="blurbs" className={`${panelClass} modern-profile-skeleton-blurbs`} key="blurbs">
        <span className="modern-skeleton-line is-title" />
        <span className="modern-skeleton-line" />
        <span className="modern-skeleton-line" />
        <span className="modern-skeleton-line is-short" />
        <span className="modern-skeleton-line is-title" />
        <span className="modern-skeleton-line" />
      </section>
    ),
    friends: (
      <section id="friends" className={`${panelClass} modern-profile-skeleton-friends`} key="friends">
        <span className="modern-skeleton-line is-title" />
        <div className="friends-grid">
          {Array.from({ length: 8 }, (_, index) => (
            <span className="friend modern-skeleton-friend" key={index} />
          ))}
        </div>
      </section>
    ),
    comments: (
      <section id="comments-section" className={`${panelClass} modern-profile-skeleton-comments`} key="comments">
        <span className="modern-skeleton-line is-title" />
        <span className="modern-skeleton-line" />
        <span className="modern-skeleton-line is-short" />
      </section>
    ),
    replies: null,
  };
  return (
    <CustomizationFrame theme={theme} effectsEnabled={false}>
      <div
        className="modern-profile-skeleton"
        data-testid="modern-profile-loading"
        aria-busy="true"
      >
        <span className="sr-only" role="status">Opening @{handle}</span>
        <div className="main-content classic-profile-grid">
          <div className="left-column">
            <section id="profile-name-section" className={panelClass}>
              {profile ? (
                <div className="profile-text-container">
                  <div id="profile-name-header" role="heading" aria-level={1}>
                    {profile.displayName || profile.handle}
                  </div>
                  <span id="profile-address">@{profile.handle}</span>
                </div>
              ) : (
                <div className="modern-skeleton-lines">
                  <span className="modern-skeleton-line is-title" />
                  <span className="modern-skeleton-line is-short" />
                </div>
              )}
            </section>
            <section id="profile-pic-info" className="profile-container">
              <div className="modern-profile-skeleton-avatar modern-skeleton-block" />
              <div className={`profile-info ${panelClass}`}>
                <div className="modern-skeleton-lines">
                  <span className="modern-skeleton-line is-short" />
                  <span className="modern-skeleton-line" />
                  <span className="modern-skeleton-line" />
                  <span className="modern-skeleton-line is-short" />
                </div>
              </div>
            </section>
            <section id="contact" className={`${panelClass} modern-profile-skeleton-contact`}>
              <span className="modern-skeleton-line is-title" />
              <div className="modern-profile-skeleton-actions">
                <span className="modern-skeleton-line" />
                <span className="modern-skeleton-line" />
                <span className="modern-skeleton-line" />
                <span className="modern-skeleton-line" />
              </div>
            </section>
            {layout.left
              .filter(section => !layout.hidden.includes(section))
              .map(section => isProfileSectionId(section) ? sections[section] : null)}
          </div>
          <div className="right-column">
            {layout.right
              .filter(section => !layout.hidden.includes(section))
              .map(section => isProfileSectionId(section) ? sections[section] : null)}
          </div>
        </div>
      </div>
    </CustomizationFrame>
  );
}

function MusicPlayer({ music }: { music: ProfileMusic }) {
  const [bandcampEmbed, setBandcampEmbed] = useState<string>();
  const [midiStatus, setMidiStatus] = useState<'idle' | 'loading' | 'playing' | 'paused' | 'error'>('idle');
  const midiPlayer = useRef<{ play(): void; pause(): void } | undefined>(undefined);
  useEffect(() => {
    trackSiteEvent('music_preview', { provider: music.provider });
  }, [music.provider, music.url]);

  useEffect(() => {
    if (music.provider !== 'bandcamp') return;
    let disposed = false;
    fetch(`../bandcamp-proxy.php?url=${encodeURIComponent(music.url)}`)
      .then(response => {
        if (!response.ok) throw new Error(`Bandcamp returned HTTP ${response.status}`);
        return response.text();
      })
      .then(html => {
        const encoded = html.match(/<meta\s+name=["']bc-page-properties["']\s+content=["']([^"']+)["']/i)?.[1];
        if (!encoded) return;
        const properties = JSON.parse(encoded.replace(/&quot;/g, '"')) as {
          item_id?: number | string;
          item_type?: string;
        };
        if (!properties.item_id || disposed) return;
        const type = properties.item_type === 'a' ? 'album' : 'track';
        setBandcampEmbed(`https://bandcamp.com/EmbeddedPlayer/${type}=${properties.item_id}/size=large/bgcol=ffffff/linkcol=0687f5/tracklist=false/transparent=true/`);
      })
      .catch(() => undefined);
    return () => {
      disposed = true;
    };
  }, [music.provider, music.url]);

  const toggleMidi = async () => {
    if (midiPlayer.current) {
      if (midiStatus === 'playing') {
        midiPlayer.current.pause();
        setMidiStatus('paused');
      } else {
        midiPlayer.current.play();
        setMidiStatus('playing');
      }
      return;
    }
    setMidiStatus('loading');
    try {
      const loadScript = (src: string) => new Promise<void>((resolve, reject) => {
        const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
        if (existing?.dataset.loaded === 'true') return resolve();
        const script = existing || document.createElement('script');
        script.src = src;
        script.addEventListener('load', () => {
          script.dataset.loaded = 'true';
          resolve();
        }, { once: true });
        script.addEventListener('error', () => reject(new Error(`Could not load ${src}`)), { once: true });
        if (!existing) document.head.appendChild(script);
      });
      await loadScript('../vendor/JZZ.js');
      await loadScript('../vendor/JZZ.midi.SMF.js');
      await loadScript('../vendor/JZZ.synth.Tiny.js');
      const response = await fetch(music.url);
      if (!response.ok) throw new Error(`MIDI returned HTTP ${response.status}`);
      const JZZ = (window as unknown as Window & {
        JZZ: {
          MIDI: { SMF: new (data: ArrayBuffer) => { player(): { connect(output: unknown): { play(): void; pause(): void } } } };
          synth: { Tiny(): unknown };
        };
      }).JZZ;
      const player = new JZZ.MIDI.SMF(await response.arrayBuffer()).player();
      const connected = player.connect(JZZ.synth.Tiny());
      midiPlayer.current = connected;
      connected.play();
      setMidiStatus('playing');
    } catch {
      setMidiStatus('error');
    }
  };

  if (music.provider === 'bandcamp' && bandcampEmbed) {
    return (
      <iframe
        className="music-player music-player-bandcamp"
        src={bandcampEmbed}
        title="Listen on Bandcamp"
        loading="lazy"
      />
    );
  }
  if (music.provider === 'midi') {
    return (
      <div id="music-info">
        <button type="button" className="classic-text-button" onClick={() => void toggleMidi()} disabled={midiStatus === 'loading'}>
          {midiStatus === 'playing' ? 'Pause MIDI' : midiStatus === 'loading' ? 'Loading MIDI…' : 'Play MIDI'}
        </button>
        {midiStatus === 'error' && <p role="alert">This MIDI file could not be played.</p>}
      </div>
    );
  }
  if (music.embedUrl) {
    return (
      <iframe
        className={`music-player music-player-${music.provider}`}
        src={music.embedUrl}
        title={`Listen on ${music.provider}`}
        loading="lazy"
        allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
      />
    );
  }
  return (
    <p id="music-info">
      <a href={music.url} target="_blank" rel="noreferrer">
        Play this profile&apos;s music on {music.provider}
      </a>
    </p>
  );
}

function ProfilePostThread({
  post,
  interactive = true,
}: {
  post: FeedPost;
  interactive?: boolean;
}) {
  const location = useLocation();
  return (
    <article className="modern-profile-post" style={{ marginTop: 0 }}>
      {post.record.createdAt && (
        <div className="post-date">
          <Link
            to={{ pathname: location.pathname, search: postSearch(post.author.handle, post.uri) }}
            style={{ color: 'inherit' }}
          >
            {formatDate(post.record.createdAt)}
          </Link>
        </div>
      )}
      <p className="modern-rich-text">
        <RichText text={post.record.text} facets={post.record.facets} autoLink />
      </p>
      <PostEmbed post={post} />
      {interactive && <PostInteractions post={post} defaultRepliesOpen />}
    </article>
  );
}

export function ProfilePage({
  handle,
  postRkey,
  postCollection = 'app.bsky.feed.post',
}: {
  handle: string;
  postRkey?: string;
  postCollection?: 'app.bsky.feed.post' | 'me.skyspace.profile.comment';
}) {
  const [gallery, setGallery] = useState<'avatar' | 'pictures' | 'videos'>();
  const [rankDialog, setRankDialog] = useState(false);
  const [appearancePreviewTheme, setAppearancePreviewTheme] = useState<ClassicTheme>();
  const [layoutPreview, setLayoutPreview] = useState<LayoutPreview>();
  const [recentPeopleSeed] = useState(Math.random);
  const cachedTheme = useMemo(() => cachedProfileTheme(handle), [handle]);
  const auth = useAuth();
  const { settings } = useSettings();
  const routerLocation = useLocation();
  const idleSections = useIdleSections();
  useLayoutEffect(() => {
    if (postRkey) window.scrollTo(0, 0);
  }, [handle, postCollection, postRkey]);
  useEffect(() => {
    setAppearancePreviewTheme(undefined);
    setLayoutPreview(undefined);
    setGallery(undefined);
  }, [handle, postRkey]);
  const profileQuery = useQuery({
    queryKey: queryKeys.profile(handle),
    queryFn: ({ signal }) => publicBluesky.getProfile(handle, signal),
  });
  const selectedRecordUri = profileQuery.data?.did && postRkey
    ? `at://${profileQuery.data.did}/${postCollection}/${postRkey}`
    : undefined;
  const selectedPostUri = postCollection === 'app.bsky.feed.post'
    ? selectedRecordUri
    : undefined;
  const selectedPostQuery = useQuery({
    queryKey: ['post-thread', selectedPostUri],
    queryFn: ({ signal }) => publicBluesky.getPostThread(selectedPostUri!, 1, signal),
    enabled: Boolean(selectedPostUri),
    staleTime: 30_000,
    retry: false,
  });
  const selectedCommentQuery = useQuery({
    queryKey: ['profile-comment-record', selectedRecordUri],
    queryFn: ({ signal }) => publicBluesky.getRecord<{
      text?: string;
      createdAt?: string;
    }>(
      profileQuery.data!.did,
      'me.skyspace.profile.comment',
      postRkey!,
      signal,
    ),
    enabled: Boolean(
      selectedRecordUri
      && postCollection === 'me.skyspace.profile.comment',
    ),
    staleTime: 30_000,
    retry: false,
  });
  const selectedCommentPost = useMemo((): FeedPost | undefined => {
    const record = selectedCommentQuery.data;
    const author = profileQuery.data;
    if (!record || !author) return undefined;
    return {
      uri: record.uri,
      cid: record.cid || record.uri,
      author,
      record: {
        text: record.value.text,
        createdAt: record.value.createdAt,
      },
    };
  }, [profileQuery.data, selectedCommentQuery.data]);
  const feedQuery = useQuery({
    queryKey: [...queryKeys.authorFeed(handle), auth.did || 'public'],
    queryFn: ({ signal }) => auth.rpc && auth.did
      ? getAuthenticatedAuthorFeed(auth.rpc, handle, 100)
      : publicBluesky.getAuthorFeed(handle, 100, signal),
  });
  const contentQuery = useQuery({
    queryKey: queryKeys.skyspaceContent(profileQuery.data?.did || ''),
    queryFn: ({ signal }) => publicBluesky.getSkyspaceContent(profileQuery.data!.did, signal),
    enabled: Boolean(profileQuery.data?.did),
    retry: false,
  });
  const siteModerationQuery = useQuery({
    queryKey: queryKeys.siteModeration,
    queryFn: ({ signal }) => getSiteModeration(signal),
    staleTime: 30_000,
  });
  const blockedHandles = useMemo(
    () => new Set(siteModerationQuery.data?.blockedUsers || []),
    [siteModerationQuery.data?.blockedUsers],
  );

  const sourceFeed = feedQuery.data?.feed || [];
  const profileMediaHidden = profileQuery.data
    ? shouldHideActor(profileQuery.data, settings.showSensitiveContent, Boolean(auth.did))
    : false;
  useEffect(() => {
    if (profileMediaHidden) {
      trackSiteEvent('moderation_hidden', { profile: profileQuery.data?.handle });
    }
  }, [profileMediaHidden, profileQuery.data?.handle]);
  const feed = useMemo(
    () => sourceFeed.filter(item => !shouldHidePost(
      item.post,
      settings.showSensitiveContent,
      Boolean(auth.did),
    )),
    [auth.did, settings.showSensitiveContent, sourceFeed],
  );
  const content = contentQuery.data?.content?.value;
  const legacyAesthetics = useMemo(() => parseLegacyAesthetics(sourceFeed), [sourceFeed]);
  const customAesthetics = contentQuery.data?.aesthetics?.value;
  const aesthetics = useMemo(
    () => mergeProfileAesthetics(legacyAesthetics, customAesthetics),
    [customAesthetics, legacyAesthetics],
  );
  const theme = useMemo(
    () => normalizeClassicTheme(aesthetics),
    [aesthetics],
  );
  const themeDataPending = feedQuery.isPending
    || (Boolean(profileQuery.data?.did) && contentQuery.isPending);
  const preparedFont = usePreparedGoogleFont(
    themeDataPending && cachedTheme ? cachedTheme.font : theme.font,
  );
  const visibleTheme = appearancePreviewTheme || theme;
  const storedCustomPanels = useMemo(
    () => customPanelSlots(content?.customPanels),
    [content?.customPanels],
  );
  const customPanels = layoutPreview?.customPanels || storedCustomPanels;
  const storedLayout = useMemo(
    () => normalizeProfileLayout(aesthetics?.layout, storedCustomPanels),
    [aesthetics?.layout, storedCustomPanels],
  );
  const layout = layoutPreview?.layout || storedLayout;
  const interests = useMemo(
    () => parseInterests(sourceFeed, content?.interests),
    [content?.interests, sourceFeed],
  );
  const music = useMemo(() => parseMusic(sourceFeed, content?.music), [content?.music, sourceFeed]);
  const pictures = useMemo(() => extractPictures(feed), [feed]);
  const videos = useMemo(() => extractVideos(feed), [feed]);
  const posts = useMemo(() => regularPosts(feed), [feed]);
  const whoToMeet = content?.wiltm !== undefined ? content.wiltm : legacyWhoToMeet(sourceFeed);
  const top8Slots = useMemo(
    () => parseTop8Slots(sourceFeed, content?.top8),
    [content?.top8, sourceFeed],
  );
  const forcedTop8 = useMemo(
    () => parseTop8Handles(sourceFeed, content?.top8),
    [content?.top8, sourceFeed],
  );
  const topFriendsVisibility = [
    auth.did ? 'authenticated' : 'guest',
    settings.showSensitiveContent ? 'sensitive' : 'filtered',
    [...blockedHandles].sort().join(','),
  ].join(':');
  const topFriendsQuery = useQuery({
    queryKey: queryKeys.topFriends(
      handle,
      top8Slots.map(item => item || '-').join(','),
      topFriendsVisibility,
    ),
    queryFn: async ({ signal }): Promise<{
      profiles: ActorProfile[];
      friendCount?: number;
    }> => {
      const canDisplay = (person: ActorProfile) => (
        !blockedHandles.has(person.handle.toLowerCase())
        && !shouldHideActor(
          person,
          settings.showSensitiveContent,
          Boolean(auth.did),
        )
      );
      const [forced, mutuals] = await Promise.all([
        publicBluesky.getProfiles(forcedTop8, signal),
        publicBluesky.getMutualSummary(profileQuery.data!, 8, signal, canDisplay)
          .catch(() => ({ count: undefined, profiles: [] })),
      ]);
      return {
        profiles: selectTopFriendSlots(
          top8Slots,
          [...forced.filter(canDisplay), ...mutuals.profiles],
        ),
        friendCount: mutuals.count,
      };
    },
    enabled: Boolean(profileQuery.data)
      && !feedQuery.isPending
      && !contentQuery.isPending
      && !siteModerationQuery.isPending,
    staleTime: 60 * 60 * 1000,
  });
  const commentsQuery = useQuery({
    queryKey: queryKeys.comments(handle),
    queryFn: async ({ signal }) => {
      const [structured, legacy] = await Promise.all([
        getProfileComments(profileQuery.data!.did, signal).catch(() => []),
        publicBluesky.searchPosts(`#skyspace @${handle}`, 50, signal)
          .then(response => friendComments(response.posts || [], handle))
          .catch(() => []),
      ]);
      return [...new Map(
        [...structured, ...legacy]
          .sort((left, right) => (
            Date.parse(right.record.createdAt || '') - Date.parse(left.record.createdAt || '')
          ))
          .map(comment => [comment.uri, comment]),
      ).values()];
    },
    retry: false,
    enabled: idleSections && Boolean(profileQuery.data?.did),
  });
  const replyRoots = posts.slice(0, 3);
  const repliesQuery = useQuery({
    queryKey: queryKeys.replies(handle, replyRoots.map(post => post.uri)),
    queryFn: async ({ signal }) => Promise.all(
      replyRoots.map(post => publicBluesky.getPostThread(post.uri, 1, signal)),
    ).then(results => results.flatMap(result => result.thread ? [result.thread] : [])),
    enabled: idleSections && replyRoots.length > 0,
    retry: false,
  });
  const supportersQuery = useQuery({
    queryKey: queryKeys.supporters,
    queryFn: ({ signal }) => loadSupporters(signal),
  });
  const latestPeopleQuery = useQuery({
    queryKey: queryKeys.latestPeople,
    queryFn: async ({ signal }): Promise<ActorProfile[]> => {
      const recent = (await getRecentSkyspacePeople(signal))
        .sort((left, right) => right.discoveryAt.localeCompare(left.discoveryAt))
        .slice(0, 20);
      const profiles = await publicBluesky.getProfiles(
        recent.map(person => person.handle),
        signal,
      );
      const profilesByDid = new Map(profiles.map(profile => [profile.did, profile]));
      return recent.flatMap(person => {
        const profile = profilesByDid.get(person.did);
        return profile ? [profile] : [];
      });
    },
    enabled: profileQuery.data?.handle.toLowerCase() === 'skyspace.me' && !auth.did,
    staleTime: 30_000,
  });
  const homeRecentPeople = useMemo(() => auth.did ? [] : selectWeightedRecentPeople(
    visibleActors(
      latestPeopleQuery.data || [],
      blockedHandles,
      settings.showSensitiveContent,
      false,
    ),
    recentPeopleSeed,
    3,
  ), [
    auth.did,
    blockedHandles,
    latestPeopleQuery.data,
    recentPeopleSeed,
    settings.showSensitiveContent,
  ]);
  const homeAppearanceQueries = useQueries({
    queries: homeRecentPeople.map(person => ({
      queryKey: queryKeys.profileAppearance(person.did),
      queryFn: ({ signal }: { signal: AbortSignal }) => (
        loadSkyspaceProfileTheme(person, signal)
      ),
      staleTime: 5 * 60 * 1000,
      retry: false,
    })),
  });
  const homeAppearances = useMemo(() => Object.fromEntries(
    homeRecentPeople.map((person, index) => [
      person.did,
      homeAppearanceQueries[index]?.data || normalizeClassicTheme(undefined),
    ]),
  ), [homeAppearanceQueries, homeRecentPeople]);

  const latestPeoplePending = profileQuery.data?.handle.toLowerCase() === 'skyspace.me'
    && !auth.did
    && latestPeopleQuery.isPending;
  const presentationPending = profileQuery.isPending
    || feedQuery.isPending
    || (Boolean(profileQuery.data?.did) && contentQuery.isPending)
    || siteModerationQuery.isPending
    || supportersQuery.isPending
    || auth.pending
    || latestPeoplePending
    || !preparedFont;
  const profileInfoContentRef = useAdaptiveProfileInfoSpacing(
    `${handle}:${auth.did || 'guest'}:${presentationPending ? 'pending' : 'ready'}`,
  );

  useEffect(() => {
    if (!presentationPending && profileQuery.data) cacheProfileTheme(handle, theme);
  }, [handle, presentationPending, profileQuery.data, theme]);

  if (profileQuery.isError) {
    return <ErrorState title={`We couldn’t open @${handle}`} error={profileQuery.error} />;
  }
  if (presentationPending) {
    return (
      <ProfileLoadingSkeleton
        handle={handle}
        profile={profileQuery.data}
        theme={cachedTheme || theme}
        layout={layout}
      />
    );
  }
  if (blockedHandles.has(profileQuery.data.handle.toLowerCase())) {
    return (
      <section className="classic-state-panel" role="status">
        <h1>This profile isn’t available on SkySpace</h1>
        <p>It has been hidden by SkySpace moderation.</p>
      </section>
    );
  }

  const profile = profileQuery.data;
  const fullName = profile.displayName || profile.handle;
  const firstName = fullName.split(/\s+/)[0];
  const quote = aesthetics?.bio?.quote;
  const location = aesthetics?.bio?.location;
  const frameClass = 'modern-framed-panel';
  const showSkyspaceWelcome = shouldShowSkyspaceWelcome(
    profile.handle,
    handle,
    auth.did,
  );
  const isSupporter = isSkyspaceSupporter(supportersQuery.data, profile);
  const supporterHandleList = (supportersQuery.data || []).map(supporter => supporter.handle);
  const isOwner = auth.did === profile.did;
  const visibleCustomPanels = isSupporter
    ? customPanels.filter(panel => panel.type !== 'chat' || chatroomsEnabled())
    : [];
  const status = activityStatus(feed[0]?.post.record.createdAt);
  const visibleTopFriends = (topFriendsQuery.data?.profiles || []).filter(person => !shouldHideActor(
    person,
    settings.showSensitiveContent,
    Boolean(auth.did),
  ));
  const quoteAndLocation = (
    <>
      {quote && (
        <span id="profile-quote" className={quoteFitClass(quote).trim()}>
          <i>&quot;{quote}&quot;</i>
        </span>
      )}
      {location && (
        <span id="profile-location">
          {location.split(/,\s*/).map((term, index) => (
            <span key={`${term}-${index}`}>
              {index > 0 && ', '}
              <a href={searchSearch(term)}>{term}</a>
            </span>
          ))}
        </span>
      )}
    </>
  );
  const interestsDisplay = interests.map(group => (
    <div className="interests-row" key={group.category}>
      <div className="interests-col1">{group.category}</div>
      <div className="interests-col2">
        {group.terms.map((term, index) => (
          <span key={term}>
            {index > 0 && ', '}
            <a href={searchSearch(term)}>{term}</a>
          </span>
        ))}
      </div>
    </div>
  ));
  const editableInterests = content?.interests !== undefined
    ? content.interests
    : interests.map(group => `[${group.category}] ${group.terms.join(', ')}`).join('\n');
  const movableSections: Record<ProfileSectionId, ReactNode> = {
    music: (music || isOwner) ? (
      <section id="music" className={`music-section ${frameClass}`} data-testid="modern-music" key="music">
        {isOwner ? (
          <MusicEditor
            profile={profile}
            data={contentQuery.data}
            value={content?.music !== undefined ? content.music : music?.url || ''}
            renderPreview={value => {
              const preview = parseMusic([], value);
              return preview
                ? <MusicPlayer music={preview} />
                : <p>This URL is not a supported music provider yet.</p>;
            }}
          />
        ) : music && <MusicPlayer music={music} />}
      </section>
    ) : null,
    interests: (interests.length > 0 || isOwner) ? (
      <section id="interests-section" className={`interests-section ${frameClass}`} key="interests">
        <div className="interests-title-bar">{firstName}&apos;s Interests</div>
        <div className="interests-content">
          {isOwner ? (
            <InterestsEditor
              profile={profile}
              data={contentQuery.data}
              value={editableInterests}
            >
              {interestsDisplay}
            </InterestsEditor>
          ) : interestsDisplay}
        </div>
      </section>
    ) : null,
    latest: (
      <section
        id="latest-post"
        className="modern-profile-posts-section"
        style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
        key="latest"
      >
        {feedQuery.isPending && (
          <div className={`latest-post-section ${frameClass}`}>
            <strong id="latest-post-header">{firstName}&apos;s Latest Post</strong>
            <p>Loading latest posts...</p>
          </div>
        )}
        {feedQuery.isError && (
          <div className={`latest-post-section ${frameClass}`}>
            <strong id="latest-post-header">{firstName}&apos;s Latest Post</strong>
            <p>Latest posts temporarily unavailable.</p>
          </div>
        )}
        {!feedQuery.isPending && (
          <PostsSection
            posts={posts}
            firstName={firstName}
            heading={`${firstName}'s Latest Post`}
            frameClass={frameClass}
            composer={isOwner ? (
              <NewPostControl
                profile={profile}
                onSaved={() => {
                  void feedQuery.refetch();
                  window.setTimeout(() => void feedQuery.refetch(), 4_000);
                }}
              />
            ) : undefined}
          />
        )}
      </section>
    ),
    blurbs: (
      <section id="blurbs" key="blurbs">
        <div className="section-header">{firstName}&apos;s Blurbs</div>
        <div className={`about-section ${frameClass}`}>
          {isOwner ? (
            <DirectBlurbEditor
              profile={profile}
              data={contentQuery.data}
              section="about"
              title="About Me"
              value={profile.description || ''}
              placeholder="About me"
              note="This updates the About Me text on your Bluesky profile too."
            />
          ) : (
            <>
              <h2>About Me</h2>
              <p id="about-text" className="modern-rich-text">
                <RichText text={profile.description || 'No bio found.'} autoLink />
              </p>
            </>
          )}
          {(whoToMeet || isOwner) && (
            <>
              {isOwner ? (
                <DirectBlurbEditor
                  profile={profile}
                  data={contentQuery.data}
                  section="wiltm"
                  title="Who I’d Like To Meet"
                  value={whoToMeet || ''}
                  placeholder="Who I’d like to meet"
                />
              ) : (
                <>
                  <h2>Who I&apos;d Like To Meet</h2>
                  <p id="meet-text" className="modern-rich-text">
                    <RichText text={whoToMeet} autoLink />
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </section>
    ),
    friends: (
      <section id="friends" key="friends">
        <div className="section-header">{firstName}&apos;s Friends</div>
        <div className={`top-friends ${frameClass}`}>
          {isOwner ? (
            <Suspense fallback={(
              <TopFriends
                firstName={firstName}
                friends={[]}
                pending
                supporters={supporterHandleList}
                placeholderCount={Math.min(8, profile.followsCount || 8)}
              />
            )}>
              <Top8Picker
                profile={profile}
                data={contentQuery.data}
                firstName={firstName}
                slots={top8Slots}
                profiles={visibleTopFriends}
                friendCount={topFriendsQuery.data?.friendCount}
                friendsPending={topFriendsQuery.isPending}
                supporters={supporterHandleList}
              />
            </Suspense>
          ) : (
            <TopFriends
              firstName={firstName}
              friends={visibleTopFriends}
              friendCount={topFriendsQuery.data?.friendCount}
              pending={topFriendsQuery.isPending}
              supporters={supporterHandleList}
              placeholderCount={Math.min(8, profile.followsCount || 8)}
            />
          )}
        </div>
      </section>
    ),
    comments: (Boolean(auth.did) || Boolean(commentsQuery.data?.length)) ? (
      <section id="comments-section" key="comments">
        <div className="section-header">Comments from {firstName}&apos;s Friends</div>
        <div className={`comments-section ${frameClass}`}>
          <LeaveComment
            profile={profile}
            onSaved={() => {
              void commentsQuery.refetch();
              window.setTimeout(() => void commentsQuery.refetch(), 4_000);
            }}
          />
          <div className="friend-comments-section">
            {(commentsQuery.data || [])
              .filter(post => !blockedHandles.has(post.author.handle.toLowerCase()))
              .filter(post => !shouldHidePost(
                post,
                settings.showSensitiveContent,
                Boolean(auth.did),
              ))
              .map(post => (
                <CommentCard post={post} profileHandle={profile.handle} key={post.uri} />
              ))}
          </div>
        </div>
      </section>
    ) : null,
    replies: (
      <Replies
        key="replies"
        threads={(repliesQuery.data || []).filter(thread => !shouldHidePost(
          thread.post,
          settings.showSensitiveContent,
          Boolean(auth.did),
        ) && !blockedHandles.has(thread.post.author.handle.toLowerCase()))}
        profileHandle={profile.handle}
        firstName={firstName}
      />
    ),
  };
  const sectionNode = (section: ProfileLayoutSectionId) => {
    if (isProfileSectionId(section)) return movableSections[section];
    const id = customPanelIdFromSection(section);
    const panel = visibleCustomPanels.find(item => item.id === id);
    return panel && (
      <Suspense key={panel.id} fallback={null}>
        <CustomProfilePanel
          profile={profile}
          data={contentQuery.data}
          panel={panel}
          editable={isOwner && Boolean(isSupporter)}
          frameClass={frameClass}
        />
      </Suspense>
    );
  };

  return (
    <CustomizationFrame theme={profileMediaHidden ? {
      ...visibleTheme,
      backgroundKind: 'color',
      backgroundUrl: undefined,
      backgroundColor: '#ffffff',
    } : visibleTheme}>
      <div className="modern-profile-reveal" data-testid="modern-profile">
        {profileMediaHidden && (
          <p className="modern-sensitive-notice" role="status">
            Sensitive media on this profile is hidden. You can change this in Settings.
          </p>
        )}
        {showSkyspaceWelcome && (
          <section id="special-header-panel" className={frameClass}>
            <h2 className="special-header-title">Welcome to SkySpace!</h2>
            <p className="special-header-intro">
              SkySpace is a work-in-progress social media flashback to 2005, when the internet was good
              and part of it belonged to you. It&apos;s also an easy way to find people and make new friends on Bluesky.
            </p>
            <div className="special-content-row">
              <div className="special-left-section">
                <p className="special-header-intro">If you have a Bluesky account you already have a SkySpace! Here&apos;s what to do next.</p>
                <ol className="special-header-intro">
                  <li>Log in to see your profile, edit it and make it yours 🖼️</li>
                  <li>Add some music 🎵, interests 🦄 and select your Top 8 😳</li>
                  <li>Search to find friends by interest, location, or keyword 🔎</li>
                </ol>
                <p className="special-header-intro">
                  That&apos;s it. More questions? Try the FAQ. And look out for the shiny profiles of{' '}
                  <a href="#support" onClick={event => {
                    event.preventDefault();
                    openSupportDialog();
                  }}>
                    SkySpace supporters
                  </a>!
                </p>
              </div>
              <div className="special-right-section">
                <ul id="special-header-grid" className="profiles-grid" aria-label="Recently active SkySpace people">
                  {homeRecentPeople.map(person => (
                    <SearchProfileCard
                      person={person}
                      supporter={isSkyspaceSupporter(supportersQuery.data, person)}
                      pathname={routerLocation.pathname}
                      themed
                      compact
                      appearance={homeAppearances[person.did]}
                      key={person.did || person.handle}
                    />
                  ))}
                </ul>
              </div>
            </div>
          </section>
        )}
        {isOwner && (
          <section className={`modern-customise-header ${frameClass}`}>
            <ProfileEditor
              profile={profile}
              data={contentQuery.data}
              onAppearancePreviewChange={setAppearancePreviewTheme}
              onLayoutPreviewChange={setLayoutPreview}
              subscriber={Boolean(isSupporter)}
            />
          </section>
        )}
        <div className="main-content classic-profile-grid">
          <div className="left-column">
            <section id="profile-name-section" className={frameClass}>
              <div className="profile-text-container">
                <div id="profile-name-header" role="heading" aria-level={1}>{fullName}</div>
                <span id="profile-address">@{profile.handle}</span>
              </div>
              {isSupporter && (
                <button
                  type="button"
                  className="modern-gold-badge"
                  onClick={openSupportDialog}
                  title="SkySpace supporter"
                >
                  <img src="../goldbadge.png" alt="SkySpace supporter" />
                </button>
              )}
            </section>

            <section id="profile-pic-info" className="profile-container">
              <div className="profile-picture-container">
                <div className="profile-picture">
                  <button
                    type="button"
                    className="modern-avatar-button"
                    onClick={() => setGallery('avatar')}
                    aria-label={`Enlarge ${fullName}'s profile picture`}
                  >
                    <img
                      id="profile-pic"
                      src={profileMediaHidden ? '../placeholder.png' : profile.avatar || '../placeholder.png'}
                      alt={`${fullName}'s profile`}
                      width="200"
                      height="200"
                      decoding="async"
                    />
                  </button>
                </div>
                <div id="profile-pics-link">
                  View My:{' '}
                  <button disabled={pictures.length === 0} onClick={() => setGallery('pictures')}>
                    Pics
                  </button>
                  {videos.length > 0 && (
                    <>
                      {' | '}
                      <button onClick={() => setGallery('videos')}>
                        Videos
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div className={`profile-info ${frameClass}`}>
                <div ref={profileInfoContentRef} className="profile-info-content">
                  {isOwner ? (
                    <QuoteEditor
                      profile={profile}
                      data={contentQuery.data}
                      quote={quote || ''}
                      location={location || ''}
                    />
                  ) : quoteAndLocation}
                  <div className="stats-container">
                    <div>{count(profile.followersCount)} followers</div>
                    <div>{count(profile.followsCount)} following</div>
                    <div>{count(profile.postsCount)} posts</div>
                  </div>
                  {auth.did !== profile.did && (
                    <span
                      id="last-login"
                      className={status.online ? 'online-status' : ''}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {status.label}
                    </span>
                  )}
                </div>
              </div>
            </section>

            <section id="contact" className={`contact-section ${frameClass}`}>
              <div className="contact-title-bar">Contacting {firstName}</div>
              <div className="contact-links">
                {auth.did ? (
                  <FollowButton profile={profile} />
                ) : (
                  <a
                    href={`https://bsky.app/profile/${encodeURIComponent(profile.handle)}`}
                    className="contact-link"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <img src="../add-friend-icon.png" alt="" className="contact-icon add-friend-icon" />
                    Add to Friends
                  </a>
                )}
                <a
                  href={`https://bsky.app/intent/compose?text=${encodeURIComponent(`@${profile.handle} Hey, I just saw your profile on #skyspace...`)}`}
                  className="contact-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src="../send-message-icon.png" alt="" className="contact-icon send-message-icon" />
                  Send Message
                </a>
                <a
                  href={`https://bsky.app/intent/compose?text=${encodeURIComponent(
                    `View ${profileShareName(profile.displayName, profile.handle)}'s Skyspace: ${profileShareUrl(profile.handle)}`,
                  )}`}
                  className="contact-link"
                  target="_blank"
                  rel="noreferrer"
                >
                  <img src="../forward-icon.png" alt="" className="contact-icon forward-icon" />
                  Share User
                </a>
                <button type="button" className="contact-link modern-contact-button" onClick={() => setRankDialog(true)}>
                  <img src="../rank-user-icon.png" alt="" className="contact-icon rank-user-icon" />
                  Rank User
                </button>
              </div>
            </section>

            {layout.left
              .filter(section => !layout.hidden.includes(section))
              .map(sectionNode)}
          </div>

          <div className="right-column">
            {postRkey ? (
              <section
                id="selected-post"
                className={`latest-post-section ${frameClass}`}
              >
                <strong id="latest-post-header">
                  {firstName}&apos;s {postCollection === 'me.skyspace.profile.comment' ? 'Comment' : 'Post'}
                </strong>
                <div style={{ margin: '8px 0 12px', fontSize: 11 }}>
                  <Link to={{
                    pathname: routerLocation.pathname,
                    search: profileSearch(profile.handle),
                  }}>
                    ← View {firstName}&apos;s profile
                  </Link>
                </div>
                {(profileQuery.isPending || (
                  postCollection === 'me.skyspace.profile.comment'
                    ? selectedCommentQuery.isPending
                    : selectedPostQuery.isPending
                )) && (
                  <p>
                    Opening {postCollection === 'me.skyspace.profile.comment'
                      ? 'comment…'
                      : 'post and replies…'}
                  </p>
                )}
                {(profileQuery.isError || (
                  postCollection === 'me.skyspace.profile.comment'
                    ? selectedCommentQuery.isError
                    : selectedPostQuery.isError
                )) && (
                  <p role="alert">
                    This {postCollection === 'me.skyspace.profile.comment'
                      ? 'comment'
                      : 'post'} could not be opened.
                  </p>
                )}
                {selectedPostQuery.data?.thread?.post && (
                  <ProfilePostThread post={selectedPostQuery.data.thread.post} />
                )}
                {selectedCommentPost && (
                  <ProfilePostThread post={selectedCommentPost} interactive={false} />
                )}
              </section>
            ) : (
              layout.right
                .filter(section => !layout.hidden.includes(section))
                .map(sectionNode)
            )}
          </div>
        </div>
      </div>

      <Suspense fallback={null}>
        {gallery === 'pictures' && pictures.length > 0 && (
          <MediaModal title={`${firstName}'s pictures`} items={pictures} close={() => setGallery(undefined)} />
        )}
        {gallery === 'avatar' && (
          <MediaModal
            title={`${firstName}'s profile picture`}
            items={[{
              url: profileMediaHidden ? '../placeholder.png' : profile.avatar || '../placeholder.png',
              thumbnail: profileMediaHidden ? '../placeholder.png' : profile.avatar || '../placeholder.png',
              alt: `${fullName}'s profile`,
              caption: '',
              postUri: `profile:${profile.did}`,
            }]}
            close={() => setGallery(undefined)}
          />
        )}
        {gallery === 'videos' && videos.length > 0 && (
          <MediaModal title={`${firstName}'s videos`} items={videos} close={() => setGallery(undefined)} />
        )}
      </Suspense>
      {rankDialog && (
        <div className="overlay modern-rank-overlay" role="presentation" onMouseDown={event => {
          if (event.currentTarget === event.target) setRankDialog(false);
        }}>
          <section
            className="modal-content modern-rank-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modern-rank-title"
          >
            <header className="modern-rank-title-bar">
              <h2 id="modern-rank-title">Rank User</h2>
              <button className="classic-close" type="button" onClick={() => setRankDialog(false)} aria-label="Close">×</button>
            </header>
            <div className="modern-rank-message">
              <p>Come on! You can&apos;t just rank people!! It isn&apos;t 2005!</p>
              <button className="modern-rank-dismiss" type="button" onClick={() => setRankDialog(false)}>
                Okay, fair point
              </button>
            </div>
          </section>
        </div>
      )}
    </CustomizationFrame>
  );
}
